import type { ReactElement } from 'react';
import type { SimClock } from '../../../domain/models/contracts';
import { InfoPopover } from '../common/InfoPopover';
import type { InfoTopicId } from '../common/infoTopics';
import {
  formatMinuteOfDay,
  type FleetStatusCounts,
  type FleetUtilization,
  type OpsStatus,
} from '../../../domain/scheduling/scheduleMath';

interface FleetAnalyticsHeaderProps {
  clock: SimClock;
  utilization: FleetUtilization;
  statusCounts: FleetStatusCounts;
  selectedAircraftReg: string | null;
  selectedLegLabel: string | null;
  onAddLeg: () => void;
  onDeleteLeg: () => void;
  onScheduleMaintenance: () => void;
  onOpenAircraftAnalytics: () => void;
}

const STATUS_ROWS: ReadonlyArray<{ status: OpsStatus; label: string }> = [
  { status: 'IN_FLIGHT', label: 'In Flight' },
  { status: 'TURNAROUND', label: 'Gate Turnaround' },
  { status: 'IDLE', label: 'Idle' },
  { status: 'MAINTENANCE', label: 'Maintenance' },
];

const LEGEND: ReadonlyArray<{ className: string; label: string; topic?: InfoTopicId }> = [
  { className: 'fsg-block-flight', label: 'Scheduled flight' },
  { className: 'fsg-block-turn', label: 'Turnaround', topic: 'turnaroundBuffer' },
  { className: 'fsg-block-maint', label: 'A/C-check', topic: 'maintenanceChecks' },
  { className: 'fsg-block-conflict', label: 'Conflict' },
];

export function FleetAnalyticsHeader({
  clock,
  utilization,
  statusCounts,
  selectedAircraftReg,
  selectedLegLabel,
  onAddLeg,
  onDeleteLeg,
  onScheduleMaintenance,
  onOpenAircraftAnalytics,
}: FleetAnalyticsHeaderProps): ReactElement {
  const pct = Math.min(100, Math.max(0, utilization.utilizationPct));
  const hasAircraft = selectedAircraftReg !== null;

  return (
    <header className="fsg-header">
      <section className="fsg-card fsg-card-util" aria-label="Fleet utilization">
        <div className="fsg-card-head">
          <h3 className="fsg-card-title">
            Fleet utilization
            <InfoPopover topic="utilization" />
          </h3>
          <span className="fsg-clock" title="Simulation clock">
            Day {clock.dayIndex} · {formatMinuteOfDay(clock.minuteOfDay)}
          </span>
        </div>
        <div className="fsg-util-main">
          <span className="fsg-util-pct">{utilization.utilizationPct.toFixed(1)}%</span>
          <div
            className="fsg-meter"
            role="meter"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(pct)}
            aria-label="Fleet utilization"
          >
            <div className="fsg-meter-fill" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <p className="fsg-util-detail">
          Flying {utilization.flyingHours.toFixed(1)} h · Ground {utilization.groundHours.toFixed(1)} h
          · Maint {utilization.maintenanceHours.toFixed(1)} h
          <span className="fsg-muted"> ({utilization.aircraftCount} aircraft, per day)</span>
        </p>
      </section>

      <section className="fsg-card fsg-card-status" aria-label="Active flight status">
        <h3 className="fsg-card-title">Fleet status now</h3>
        <ul className="fsg-status-list">
          {STATUS_ROWS.map(({ status, label }) => (
            <li key={status} className="fsg-status-item">
              <span className={`fsg-dot fsg-dot-${status.toLowerCase().replace('_', '-')}`} />
              <span className="fsg-status-count">{statusCounts[status]}</span>
              <span className="fsg-status-label">{label}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="fsg-card fsg-card-actions" aria-label="Schedule actions">
        <h3 className="fsg-card-title">
          {hasAircraft ? `Selected: ${selectedAircraftReg}` : 'Select an aircraft'}
          {selectedLegLabel !== null && <span className="fsg-muted"> · {selectedLegLabel}</span>}
        </h3>
        <div className="fsg-actions">
          <button
            type="button"
            className="fsg-btn fsg-btn-primary"
            disabled={!hasAircraft}
            onClick={onAddLeg}
            title="Dispatches CREATE_FLIGHT_LEG"
          >
            + Add Flight Leg
          </button>
          <button
            type="button"
            className="fsg-btn fsg-btn-danger"
            disabled={selectedLegLabel === null}
            onClick={onDeleteLeg}
            title="Dispatches DELETE_FLIGHT_LEG"
          >
            Delete Leg
          </button>
          <button
            type="button"
            className="fsg-btn fsg-btn-maint"
            disabled={!hasAircraft}
            onClick={onScheduleMaintenance}
            title="Dispatches SCHEDULE_MAINTENANCE"
          >
            Schedule Maintenance
          </button>
          <button
            type="button"
            className="fsg-btn"
            disabled={!hasAircraft}
            onClick={onOpenAircraftAnalytics}
            title="Opens the routes this aircraft flies"
          >
            Aircraft analytics
          </button>
        </div>
        <ul className="fsg-legend" aria-label="Legend">
          {LEGEND.map((entry) => (
            <li key={entry.label} className="fsg-legend-item">
              <span className={`fsg-swatch ${entry.className}`} />
              {entry.label}
              {entry.topic !== undefined && <InfoPopover topic={entry.topic} />}
            </li>
          ))}
          <li className="fsg-legend-item">
            <span className="fsg-swatch fsg-swatch-now" />
            Now
          </li>
        </ul>
      </section>
    </header>
  );
}
